import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import X from 'lucide-react/dist/esm/icons/x';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getReviewImageUrl } from '@/lib/image-urls';

interface ImageLightboxProps {
  open: boolean;
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}

export default function ImageLightbox({
  open,
  images,
  currentIndex,
  onClose,
  onNext,
  onPrev,
}: ImageLightboxProps) {
  const [isNavigating, setIsNavigating] = useState(false);
  const isNavigatingRef = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleNext = useCallback(
    (event?: React.MouseEvent | KeyboardEvent) => {
      if (isNavigatingRef.current) return;
      isNavigatingRef.current = true;
      setIsNavigating(true);
      if (event && 'stopPropagation' in event) {
        event.stopPropagation();
        event.preventDefault();
      }
      onNext();
      setTimeout(() => {
        isNavigatingRef.current = false;
        setIsNavigating(false);
      }, 100);
    },
    [onNext],
  );

  const handlePrev = useCallback(
    (event?: React.MouseEvent | KeyboardEvent) => {
      if (isNavigatingRef.current) return;
      isNavigatingRef.current = true;
      setIsNavigating(true);
      if (event && 'stopPropagation' in event) {
        event.stopPropagation();
        event.preventDefault();
      }
      onPrev();
      setTimeout(() => {
        isNavigatingRef.current = false;
        setIsNavigating(false);
      }, 100);
    },
    [onPrev],
  );

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.currentTarget;
    target.style.border = '2px solid red';
    target.alt = 'Failed to load image';
  };

  useEffect(() => {
    if (!open) return;

    const handleKeydown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          handlePrev();
          break;
        case 'ArrowRight':
          event.preventDefault();
          handleNext();
          break;
      }
    };

    // Focus the dialog when opened
    if (dialogRef.current) {
      dialogRef.current.focus();
    }

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [open, onClose, handlePrev, handleNext]);

  if (!open || images.length === 0) {
    return null;
  }

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
      role="dialog"
      aria-label="Image viewer"
    >
      <div className="relative max-h-screen-lg max-w-screen-lg p-4">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full bg-black bg-opacity-50 p-2 text-white transition-all hover:bg-opacity-70"
          aria-label="Close image viewer"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Previous button */}
        {images.length > 1 && currentIndex > 0 && (
          <button
            onClick={(e) => handlePrev(e)}
            className={`absolute left-4 top-1/2 z-10 -translate-y-1/2 cursor-pointer rounded-full bg-black bg-opacity-50 p-3 text-white transition-all hover:bg-opacity-70 ${
              isNavigating ? 'pointer-events-none opacity-50' : ''
            }`}
            aria-label="Previous image"
            type="button"
            disabled={isNavigating}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}

        {/* Next button */}
        {images.length > 1 && currentIndex < images.length - 1 && (
          <button
            onClick={(e) => handleNext(e)}
            className={`absolute right-4 top-1/2 z-10 -translate-y-1/2 cursor-pointer rounded-full bg-black bg-opacity-50 p-3 text-white transition-all hover:bg-opacity-70 ${
              isNavigating ? 'pointer-events-none opacity-50' : ''
            }`}
            aria-label="Next image"
            type="button"
            disabled={isNavigating}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}

        <img
          src={getReviewImageUrl(images[currentIndex])}
          alt="Review"
          className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation();
            }
          }}
          onError={handleImageError}
        />

        {/* Image counter */}
        {images.length > 1 && (
          <div className="absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-full bg-black bg-opacity-50 px-3 py-1 text-sm text-white">
            {currentIndex + 1} / {images.length}
          </div>
        )}

        {/* Instructions */}
        <div className="absolute bottom-4 left-4 right-4 text-center">
          <p className="inline-block rounded-lg bg-black bg-opacity-50 px-3 py-2 text-sm text-white">
            {images.length > 1
              ? 'ESC לסגירה • חצים לניווט • לחצו על הרקע לסגירה'
              : 'לחצו על התמונה או ESC לסגירה'}
          </p>
        </div>
      </div>
    </div>
  );
}
